import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { getGateways } from '../../stores/actions/installerActions';
import { DataTable } from 'primereact/datatable';
import { DataView } from 'primereact/dataview';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { escapeDataTableCsvValue, excelTextDataTableCsvValue, exportDataTableCsv } from '../../helpers/exportCsv';
import { Tooltip } from 'primereact/tooltip';
import { FilterMatchMode } from 'primereact/api';
import { Link } from 'react-router-dom';
import AccessTime from '@material-ui/icons/AccessTime';
import Check from '@material-ui/icons/Check';
import Close from '@material-ui/icons/Close';
import { getSimOperator } from '../../helpers/simOperator';

type StatusColor = 'green' | 'yellow' | 'red' | 'gray';

interface GatewayRow {
  id: string;
  system: string | null;
  systemID: number | null;
  name: string | null;
  systemVersion: string;
  serial: string | null;
  router: string | null;
  eth0ip: string | null;
  vpn2ip: string | null;
  publicAddress: string | null;
  wwan0ip: string | null;
  gatewayip: string | null;
  sim: string | null;
  apn: string | null;
  iccid: string | null;
  operator: string | null;
  simPresent: boolean | null;
  simPinRequired: boolean | null;
  batteryOperated: boolean | null;
  firmware: string | null;
  latestPacketTimestamp: string | null;
  statusReporterTimestamp: string | null;
  externalSynchronizationAt: string | null;
  statusColor: StatusColor;
  statusTooltip: string;
}

type ColumnKey =
  | 'system' | 'name' | 'systemVersion' | 'serial' | 'router' | 'iccid' | 'operator' | 'mains' | 'firmware' | 'status'
  | 'publicAddress'
  | 'eth0ip' | 'vpn2ip' | 'wwan0ip' | 'gatewayip' | 'sim' | 'apn' | 'simInserted' | 'simPinUnlocked';

const ALWAYS_VISIBLE: ColumnKey[] = ['system', 'name', 'systemVersion', 'serial', 'router', 'iccid', 'operator', 'mains', 'firmware', 'status', 'publicAddress'];
const LORA_ONLY: ColumnKey[] = ['eth0ip', 'vpn2ip', 'wwan0ip', 'gatewayip', 'sim', 'apn', 'simInserted', 'simPinUnlocked'];

const GATEWAYS_COLUMNS_STORAGE_KEY = 'gatewaysList.visibleColumns';

const loadVisibleColumns = (): ColumnKey[] => {
  try {
    const raw = localStorage.getItem(GATEWAYS_COLUMNS_STORAGE_KEY);
    if (!raw) return [...ALWAYS_VISIBLE];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...ALWAYS_VISIBLE];
    const validKeys = new Set(columnOptionsAll.map((opt) => opt.value));
    const filtered = parsed.filter((v) => validKeys.has(v));
    return filtered.length > 0 ? filtered : [...ALWAYS_VISIBLE];
  } catch {
    return [...ALWAYS_VISIBLE];
  }
};

const columnOptionsAll = [
  { label: 'System', value: 'system' },
  { label: 'Node name', value: 'name' },
  { label: 'System ver.', value: 'systemVersion' },
  { label: 'Device EUI', value: 'serial' },
  { label: 'Router', value: 'router' },
  { label: 'ICCID', value: 'iccid' },
  { label: 'SIM operator', value: 'operator' },
  { label: 'Mains powered', value: 'mains' },
  { label: 'Firmware ver.', value: 'firmware' },
  { label: 'Status', value: 'status' },
  { label: 'Public IP', value: 'publicAddress' },
  { label: 'ETH0 IP', value: 'eth0ip' },
  { label: 'TUN0 IP', value: 'vpn2ip' },
  { label: 'WWAN0 IP', value: 'wwan0ip' },
  { label: 'Gateway IP', value: 'gatewayip' },
  { label: 'SIM PIN', value: 'sim' },
  { label: 'APN', value: 'apn' },
  { label: 'SIM inserted', value: 'simInserted' },
  { label: 'SIM PIN unlocked', value: 'simPinUnlocked' },
];

const yesNoOptions = [
  { label: 'Y', value: 'Y' },
  { label: 'N', value: 'N' },
];

const statusColorOptions = [
  { label: 'Green', value: 'green' },
  { label: 'Yellow', value: 'yellow' },
  { label: 'Red', value: 'red' },
  { label: 'Gray', value: 'gray' },
];

const systemVersionFilterOptions = [
  { label: 'WSN', value: 'WSN' },
  { label: 'LoRa', value: 'LoRa' },
];

// Computes status color + tooltip text once per row, shared between desktop/mobile/filtering.
const computeStatus = (
  latestPacketTimestamp: string | null,
  statusReporterTimestamp: string | null,
  externalSynchronizationAt: string | null,
  systemVersion: string,
  syncSetting: string
): { color: StatusColor; tooltip: string } => {
  const TEN_MINUTES_MS = 10 * 60 * 1000;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const now = Date.now();

  if (systemVersion === 'W2') {
    const esa = externalSynchronizationAt
      ? new Date(externalSynchronizationAt).toLocaleString('sv-SE').replace('T', ' ')
      : '--';
    // systems not synced on-line are expected to have an old sync time
    if (['sms-only', 'off-line'].includes(syncSetting)) {
      return { color: 'gray', tooltip: `Sync time: ${esa}` };
    }
    const isSyncOld = !externalSynchronizationAt || now - new Date(externalSynchronizationAt).getTime() > DAY_MS;
    return { color: isSyncOld ? 'red' : 'green', tooltip: `Sync time: ${esa}` };
  }

  const lpt = latestPacketTimestamp
    ? new Date(latestPacketTimestamp).toLocaleString('sv-SE').replace('T', ' ')
    : '--';
  const srt = statusReporterTimestamp
    ? new Date(statusReporterTimestamp).toLocaleString('sv-SE').replace('T', ' ')
    : '--';

  const isLatestOld = !latestPacketTimestamp || now - new Date(latestPacketTimestamp).getTime() > TEN_MINUTES_MS;
  const isReporterOld = !statusReporterTimestamp || now - new Date(statusReporterTimestamp).getTime() > TEN_MINUTES_MS;

  let color: StatusColor;
  if (!isLatestOld && !isReporterOld) color = 'green';
  else if (isLatestOld && isReporterOld) color = 'red';
  else color = 'yellow';

  return { color, tooltip: `Chirpstack: ${lpt}\nStatus reporter: ${srt}` };
};

const renderStatusIcon = (row: GatewayRow) => (
  <>
    <Tooltip target={`.status-icon-${row.id}`} className="status-tooltip" />
    <div className={`status-icon-${row.id}`} data-pr-tooltip={row.statusTooltip} style={{ color: row.statusColor, textAlign: 'center' }}>
      <AccessTime />
    </div>
  </>
);

const yesNoIcon = (value: boolean | null, invert = false) => {
  if (value === null || value === undefined) return null;
  const positive = invert ? !value : value;
  return positive ? <Check style={{ color: 'green' }} /> : <Close style={{ color: 'red' }} />;
};

interface IGatewaysListProps {
  mobileView: boolean;
  gateways: any[];
  getGateways: () => void;
}

const createFilters = () => ({
  system: { value: null, matchMode: FilterMatchMode.CONTAINS },
  name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  systemVersion: { value: null, matchMode: FilterMatchMode.EQUALS },
  serial: { value: null, matchMode: FilterMatchMode.CONTAINS },
  router: { value: null, matchMode: FilterMatchMode.CONTAINS },
  iccid: { value: null, matchMode: FilterMatchMode.CONTAINS },
  operator: { value: null, matchMode: FilterMatchMode.EQUALS },
  mainsLabel: { value: null, matchMode: FilterMatchMode.EQUALS },
  firmware: { value: null, matchMode: FilterMatchMode.CONTAINS },
  statusColor: { value: null, matchMode: FilterMatchMode.EQUALS },
  publicAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
  eth0ip: { value: null, matchMode: FilterMatchMode.CONTAINS },
  vpn2ip: { value: null, matchMode: FilterMatchMode.CONTAINS },
  wwan0ip: { value: null, matchMode: FilterMatchMode.CONTAINS },
  gatewayip: { value: null, matchMode: FilterMatchMode.CONTAINS },
  sim: { value: null, matchMode: FilterMatchMode.CONTAINS },
  apn: { value: null, matchMode: FilterMatchMode.EQUALS },
  simInsertedLabel: { value: null, matchMode: FilterMatchMode.EQUALS },
  simPinUnlockedLabel: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const getUrlParams = () => new URLSearchParams(window.location.search);

const buildFiltersFromUrl = (): ReturnType<typeof createFilters> => {
  const urlParams = getUrlParams();
  const defaults = createFilters();
  const result: any = { ...defaults };
  Object.keys(defaults).forEach((key) => {
    const raw = urlParams.get(key);
    if (raw === null) return;
    result[key] = { value: raw, matchMode: (defaults as any)[key].matchMode };
  });
  return result;
};

const MOBILE_FILTER_FIELDS = ['system', 'name', 'serial', 'router', 'iccid'] as const;

const buildMobileFiltersFromUrl = () => {
  const urlParams = getUrlParams();
  const result: any = { system: '', name: '', serial: '', router: '', iccid: '' };
  MOBILE_FILTER_FIELDS.forEach((key) => {
    result[key] = urlParams.get(`m_${key}`) || '';
  });
  return result;
};

const GatewaysList = ({ mobileView, gateways, getGateways }: IGatewaysListProps) => {
  useEffect(() => {
    getGateways();
  }, [getGateways]);

  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(loadVisibleColumns);
  const [filters, setFilters] = useState(buildFiltersFromUrl);
  const [sortField, setSortField] = useState<string | undefined>(() => getUrlParams().get('sortField') || undefined);
  const [sortOrder, setSortOrder] = useState<1 | -1 | undefined>(() => {
    const v = getUrlParams().get('sortOrder');
    if (v === '1') return 1;
    if (v === '-1') return -1;
    return undefined;
  });
  const dt = useRef(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const topScrollContentRef = useRef<HTMLDivElement>(null);

  const [mobileFilters, setMobileFilters] = useState(buildMobileFiltersFromUrl);
  const [pendingFilters, setPendingFilters] = useState(mobileFilters);
  const [mobileSortField, setMobileSortField] = useState(() => getUrlParams().get('mSortField') || 'default');
  const [mobileSortOrder, setMobileSortOrder] = useState<-1 | 1>(() => (getUrlParams().get('mSortOrder') === '-1' ? -1 : 1));
  const [pendingSortField, setPendingSortField] = useState(mobileSortField);
  const [pendingSortOrder, setPendingSortOrder] = useState(mobileSortOrder);
  const [mobileFilterDialog, setMobileFilterDialog] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(GATEWAYS_COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch {
    }
  }, [visibleColumns]);

  useEffect(() => {
    const params = new URLSearchParams();

    const defaults = createFilters();
    Object.keys(defaults).forEach((key) => {
      const curVal = (filters as any)[key]?.value ?? null;
      if (curVal !== null && curVal !== '') {
        params.set(key, String(curVal));
      }
    });
    if (sortField) params.set('sortField', sortField);
    if (sortOrder) params.set('sortOrder', String(sortOrder));

    MOBILE_FILTER_FIELDS.forEach((key) => {
      const value = (mobileFilters as any)[key];
      if (value) params.set(`m_${key}`, value);
    });
    if (mobileSortField !== 'default') params.set('mSortField', mobileSortField);
    if (mobileSortOrder !== 1) params.set('mSortOrder', String(mobileSortOrder));

    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? '?' + query : ''}${window.location.hash}`;
    window.history.replaceState(null, '', newUrl);
  }, [filters, sortField, sortOrder, mobileFilters, mobileSortField, mobileSortOrder]);

  useEffect(() => {
    const wrapperEl = tableWrapperRef.current?.querySelector<HTMLDivElement>('.p-datatable-wrapper');
    const topEl = topScrollRef.current;
    const topContentEl = topScrollContentRef.current;
    if (!wrapperEl || !topEl || !topContentEl) return;

    const syncWidth = () => {
      topContentEl.style.width = `${wrapperEl.scrollWidth}px`;
    };
    syncWidth();

    let syncing = false;
    const onTopScroll = () => {
      if (syncing) return;
      syncing = true;
      wrapperEl.scrollLeft = topEl.scrollLeft;
      syncing = false;
    };
    const onTableScroll = () => {
      if (syncing) return;
      syncing = true;
      topEl.scrollLeft = wrapperEl.scrollLeft;
      syncing = false;
    };

    const resizeObserver = new ResizeObserver(syncWidth);
    resizeObserver.observe(wrapperEl);
    topEl.addEventListener('scroll', onTopScroll);
    wrapperEl.addEventListener('scroll', onTableScroll);

    return () => {
      resizeObserver.disconnect();
      topEl.removeEventListener('scroll', onTopScroll);
      wrapperEl.removeEventListener('scroll', onTableScroll);
    };
  }, [gateways, visibleColumns]);

  const rows: GatewayRow[] = useMemo(() => {
    return (gateways ?? []).map((gateway) => {
      const systemVersionLabel = gateway.SystemVersion === 'W2' ? 'WSN' : 'LoRa';
      const { color, tooltip } = computeStatus(
        gateway.LatestPacketTimestamp,
        gateway.StatusReporterTimestamp,
        gateway.ExternalSynchronizationAt,
        gateway.SystemVersion,
        gateway.SyncSetting
      );

      return {
        id: String(gateway.ID),
        system: gateway.SystemName,
        systemID: gateway.SystemID,
        name: gateway.Name,
        systemVersion: systemVersionLabel,
        serial: gateway.SerialString,
        router: gateway.RouterSerialNumber,
        eth0ip: gateway.Eth0Ip,
        vpn2ip: gateway.Vpn2Ip,
        publicAddress: gateway.W2PublicAddress,
        wwan0ip: gateway.Wwan0Ip,
        gatewayip: gateway.GatewayIp,
        sim: gateway.SimPin,
        apn: gateway.Apn,
        iccid: gateway.Iccid,
        operator: getSimOperator(gateway.Iccid),
        simPresent: gateway.SimPresent,
        simPinRequired: gateway.SimPinRequired,
        batteryOperated: gateway.BatteryOperated,
        firmware: gateway.FirmwareVersion,
        latestPacketTimestamp: gateway.LatestPacketTimestamp,
        statusReporterTimestamp: gateway.StatusReporterTimestamp,
        externalSynchronizationAt: gateway.ExternalSynchronizationAt,
        statusColor: color,
        statusTooltip: tooltip,
      };
    });
  }, [gateways]);

  // derived label fields used purely for filtering (PrimeReact filters read raw field values, not body() output)
  const rowsWithFilterFields = useMemo(() => {
    return rows.map((r) => ({
      ...r,
      mainsLabel: r.batteryOperated === null ? null : r.batteryOperated ? 'N' : 'Y',
      simInsertedLabel: r.simPresent === null ? null : r.simPresent ? 'Y' : 'N',
      simPinUnlockedLabel: r.simPinRequired === null ? null : r.simPinRequired ? 'N' : 'Y',
    }));
  }, [rows]);

  const isVisible = (key: ColumnKey) => visibleColumns.includes(key);

  const mainsFilterElement = (options: any) => (
    <Dropdown value={options.value} options={yesNoOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Y/N" showClear className="w-full" />
  );
  const simInsertedFilterElement = (options: any) => (
    <Dropdown value={options.value} options={yesNoOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Y/N" showClear className="w-full" />
  );
  const simPinUnlockedFilterElement = (options: any) => (
    <Dropdown value={options.value} options={yesNoOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Y/N" showClear className="w-full" />
  );
  const statusFilterElement = (options: any) => (
    <Dropdown value={options.value} options={statusColorOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Any" showClear className="w-full" />
  );

  // Dynamic options: only show operators/APNs that actually appear in the current data
  const operatorOptions = useMemo(() => {
    const unique = Array.from(new Set(rowsWithFilterFields.map((r) => r.operator).filter(Boolean)));
    unique.sort();
    return unique.map((v) => ({ label: v as string, value: v as string }));
  }, [rowsWithFilterFields]);

  const apnOptions = useMemo(() => {
    const unique = Array.from(new Set(rowsWithFilterFields.map((r) => r.apn).filter(Boolean)));
    unique.sort();
    return unique.map((v) => ({ label: v as string, value: v as string }));
  }, [rowsWithFilterFields]);

  const systemVersionFilterElement = (options: any) => (
    <Dropdown value={options.value} options={systemVersionFilterOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Any" showClear className="w-full" />
  );
  const operatorFilterElement = (options: any) => (
    <Dropdown value={options.value} options={operatorOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Any" showClear className="w-full" />
  );
  const apnFilterElement = (options: any) => (
    <Dropdown value={options.value} options={apnOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Any" showClear className="w-full" />
  );

  const mobileFilteredRows = useMemo(() => {
    let result = [...rowsWithFilterFields];
    if (mobileFilters.system) result = result.filter((r) => r.system?.toLowerCase().includes(mobileFilters.system.toLowerCase()));
    if (mobileFilters.name) result = result.filter((r) => r.name?.toLowerCase().includes(mobileFilters.name.toLowerCase()));
    if (mobileFilters.serial) result = result.filter((r) => r.serial?.toLowerCase().includes(mobileFilters.serial.toLowerCase()));
    if (mobileFilters.router) result = result.filter((r) => r.router?.toLowerCase().includes(mobileFilters.router.toLowerCase()));
    if (mobileFilters.iccid) result = result.filter((r) => r.iccid?.toLowerCase().includes(mobileFilters.iccid.toLowerCase()));

    // 'default' preserves the API's own order; only sort client-side if the user picked a field
    if (mobileSortField !== 'default') {
      result.sort((a, b) => {
        const aVal = a[mobileSortField];
        const bVal = b[mobileSortField];

        const aEmpty = aVal === null || aVal === undefined || aVal === '';
        const bEmpty = bVal === null || bVal === undefined || bVal === '';

        // always push empty/null values to the end, regardless of sort direction
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;

        if (aVal < bVal) return -1 * mobileSortOrder;
        if (aVal > bVal) return 1 * mobileSortOrder;
        return 0;
      });
    }

    return result;
  }, [rowsWithFilterFields, mobileFilters, mobileSortField, mobileSortOrder]);

  const openMobileFilterDialog = () => {
    setPendingFilters(mobileFilters);
    setPendingSortField(mobileSortField);
    setPendingSortOrder(mobileSortOrder);
    setMobileFilterDialog(true);
  };
  const applyMobileFilters = () => {
    setMobileFilters(pendingFilters);
    setMobileSortField(pendingSortField);
    setMobileSortOrder(pendingSortOrder);
    setMobileFilterDialog(false);
  };
  const resetMobileFilters = () => {
    const empty = { system: '', name: '', serial: '', router: '', iccid: '' };
    setPendingFilters(empty);
    setPendingSortField('default');
    setPendingSortOrder(1);
  };
  const activeFilterCount = useMemo(() => Object.values(mobileFilters).filter((v) => v !== '').length, [mobileFilters]);

  const sortOptions = [
    { label: 'Default (latest activity)', value: 'default' },
    { label: 'System', value: 'system' },
    { label: 'Node', value: 'name' },
    { label: 'Device EUI', value: 'serial' },
    { label: 'Router', value: 'router' },
    { label: 'Firmware', value: 'firmware' },
  ];

  const cardTemplate = (row: GatewayRow) => (
    <div className="col-12">
      <div className="border-1 surface-border border-round p-3 mb-2 surface-card">
        <div className="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 surface-border">
          <span className="font-bold text-lg">{row.serial || row.name}</span>
          <div className="flex align-items-center gap-2">
            <Tag value={row.systemVersion} severity={row.systemVersion === 'WSN' ? 'info' : 'secondary'} />
            {renderStatusIcon(row)}
          </div>
        </div>

        <div className="mb-2 p-2 surface-50 border-round">
          <div className="grid">
            <div className="col-6">
              <div className="text-xs text-color-secondary">System</div>
              <div className="font-medium">{row.system || '---'}</div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">Node</div>
              <div className="font-medium">{row.name || '---'}</div>
            </div>
          </div>
          <div className="grid">
          <div className="col-6">
              <div className="text-xs text-color-secondary">ICCID</div>
              <div
                className="font-medium"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={row.iccid || undefined}
              >
                {row.iccid || '---'}
              </div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">SIM operator</div>
              <div className="font-medium">{row.operator || '---'}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col-6">
              <div className="text-xs text-color-secondary">Mains powered</div>
              <div className="font-medium">{yesNoIcon(row.batteryOperated, true) ?? '---'}</div>
            </div>
            <div className="col-6">
              <div className="text-xs text-color-secondary">Firmware</div>
              <div className="font-medium">{row.firmware || '---'}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col-6">
              <div className="text-xs text-color-secondary">Router</div>
              <div className="font-medium">{row.router || '---'}</div>
            </div>
          </div>
        </div>

        {row.systemVersion === 'WSN' && (
          <div className="mb-2 p-2 surface-50 border-round">
            <div className="grid">
              <div className="col-6">
                <div className="text-xs text-color-secondary">Public IP</div>
                <div className="font-medium">{row.publicAddress || '---'}</div>
              </div>
              <div className="col-6">
                <div className="text-xs text-color-secondary">TUN0 IP</div>
                <div className="font-medium">{row.vpn2ip || '---'}</div>
              </div>
            </div>
          </div>
        )}

        {row.systemVersion === 'LoRa' && (
          <div className="mb-2 p-2 surface-50 border-round">
            <div className="grid">
              <div className="col-6">
                <div className="text-xs text-color-secondary">ETH0 IP</div>
                <div className="font-medium">{row.eth0ip || '---'}</div>
              </div>
              <div className="col-6">
                <div className="text-xs text-color-secondary">TUN0 IP</div>
                <div className="font-medium">{row.vpn2ip || '---'}</div>
              </div>
            </div>
            <div className="grid">
              <div className="col-6">
                <div className="text-xs text-color-secondary">WWAN0 IP</div>
                <div className="font-medium">{row.wwan0ip || '---'}</div>
              </div>
              <div className="col-6">
                <div className="text-xs text-color-secondary">Gateway IP</div>
                <div className="font-medium">{row.gatewayip || '---'}</div>
              </div>
            </div>
            <div className="grid">
              <div className="col-6">
                <div className="text-xs text-color-secondary">SIM inserted</div>
                <div className="font-medium">{yesNoIcon(row.simPresent) ?? '---'}</div>
              </div>
              <div className="col-6">
                <div className="text-xs text-color-secondary">SIM PIN unlocked</div>
                <div className="font-medium">{yesNoIcon(row.simPinRequired, true) ?? '---'}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const mobileFilterBar = (
    <div className="flex align-items-center gap-2 mb-3">
      <InputText
        value={mobileFilters.system}
        onChange={(e) => setMobileFilters((prev) => ({ ...prev, system: e.target.value }))}
        placeholder="Search system..."
        className="flex-1"
      />
      <div className="relative">
        <Button
          icon="pi pi-filter"
          rounded
          outlined={activeFilterCount === 0}
          severity={activeFilterCount > 0 ? 'info' : undefined}
          onClick={openMobileFilterDialog}
        />
        {activeFilterCount > 0 && (
          <span
            className="absolute top-0 right-0 bg-primary text-white border-circle flex align-items-center justify-content-center"
            style={{ width: '1.2rem', height: '1.2rem', fontSize: '0.65rem', transform: 'translate(30%, -30%)' }}
          >
            {activeFilterCount}
          </span>
        )}
      </div>
    </div>
  );

  const mobileFilterDialogFooter = (
    <div className="flex justify-content-between">
      <Button label="Reset" icon="pi pi-times" text severity="secondary" onClick={resetMobileFilters} />
      <div className="flex gap-2">
        <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setMobileFilterDialog(false)} />
        <Button label="Apply" icon="pi pi-check" onClick={applyMobileFilters} />
      </div>
    </div>
  );

  const handleResetView = () => {
    setFilters(createFilters());
    setSortField(undefined);
    setSortOrder(undefined);
    getGateways(); // fresh data from db with default order
  };

  if (mobileView) {
    return (
      <div data-testid="GatewaysReactTable">
        {mobileFilterBar}
        <DataView value={mobileFilteredRows} itemTemplate={cardTemplate} paginator rows={10} />

        <Dialog
          visible={mobileFilterDialog}
          onHide={() => setMobileFilterDialog(false)}
          header="Filter &amp; Sort"
          style={{ width: '95vw' }}
          footer={mobileFilterDialogFooter}
          modal
          className="p-fluid"
        >
          <div className="mb-4">
            <label className="font-bold mb-2 block">Sort by</label>
            <div className="flex gap-2">
              <Dropdown value={pendingSortField} options={sortOptions} onChange={(e) => setPendingSortField(e.value)} className="flex-1" />
              <Button
                icon={pendingSortOrder === 1 ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                outlined
                disabled={pendingSortField === 'default'}
                onClick={() => setPendingSortOrder((prev) => (prev === 1 ? -1 : 1))}
                tooltip={pendingSortOrder === 1 ? 'Ascending' : 'Descending'}
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Node</label>
            <InputText value={pendingFilters.name} onChange={(e) => setPendingFilters((prev) => ({ ...prev, name: e.target.value }))} placeholder="Search..." />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Device EUI</label>
            <InputText value={pendingFilters.serial} onChange={(e) => setPendingFilters((prev) => ({ ...prev, serial: e.target.value }))} placeholder="Search..." />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">Router</label>
            <InputText value={pendingFilters.router} onChange={(e) => setPendingFilters((prev) => ({ ...prev, router: e.target.value }))} placeholder="Search..." />
          </div>
          <div className="mb-3">
            <label className="text-sm text-color-secondary mb-1 block">ICCID</label>
            <InputText value={pendingFilters.iccid} onChange={(e) => setPendingFilters((prev) => ({ ...prev, iccid: e.target.value }))} placeholder="Search..." />
          </div>
        </Dialog>
      </div>
    );
  }

  return (
    <div data-testid="GatewaysReactTable">
      <div className="flex align-items-center justify-content-end gap-2 mb-3">
        <MultiSelect
          value={visibleColumns}
          options={columnOptionsAll}
          onChange={(e) => setVisibleColumns(e.value)}
          placeholder="Columns"
          display="chip"
          className="w-20rem"
        />
        <Button icon="pi pi-refresh" label="Reset & Refresh" outlined onClick={handleResetView} />
        <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'gateways')} />
      </div>

      <div ref={topScrollRef} className="systems-table-top-scroll">
        <div ref={topScrollContentRef} style={{ height: 1 }} />
      </div>
      <div ref={tableWrapperRef}>
      <DataTable
        ref={dt}
        value={rowsWithFilterFields}
        exportFilename="gateways"
        exportFunction={(e) => e.field === 'iccid' ? excelTextDataTableCsvValue(e.data) : escapeDataTableCsvValue(e.field === 'statusColor' ? (e.rowData.statusTooltip ?? e.data) : e.data)}
        className="p-datatable-sm"
        stripedRows
        showGridlines
        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100, 200, 500]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="{first} - {last} of {totalRecords}"
        resizableColumns
        columnResizeMode="expand"
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        filterDisplay="row"
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder as 1 | -1 | undefined); }}
      >
        {isVisible('system') && (
          <Column 
            field="system" 
            header="System"
            body={(rowData) => (<div style={{paddingTop: '10px', paddingBottom: '10px'}}>
                {rowData.systemID ? <Link to={`/systems/${rowData.systemID}/dashboard`} className="text-primary">{rowData.system}</Link> : '---'}</div>
            )} 
            filter
            filterPlaceholder="" 
            showFilterMenu={false} 
            sortable 
            style={{ width: 140 }} />
        )}
        {isVisible('name') && (
          <Column field="name" header="Node name" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 130 }} />
        )}
        {isVisible('systemVersion') && (
          <Column
            field="systemVersion"
            header="Sys. ver."
            filter showFilterMenu={false}
            filterElement={systemVersionFilterElement}
            sortable
            style={{ width: 90 }}
            body={(row: GatewayRow) => <Tag value={row.systemVersion} severity={row.systemVersion === 'WSN' ? 'info' : 'secondary'} />}
          />
        )}
        {isVisible('serial') && (
          <Column field="serial" header="Device EUI" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 140 }} />
        )}
        {isVisible('router') && (
          <Column field="router" header="Router" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 130 }} />
        )}
        {isVisible('iccid') && (
          <Column field="iccid" header="ICCID" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 130 }} />
        )}
        {isVisible('operator') && (
          <Column
            field="operator"
            header="SIM operator"
            filter showFilterMenu={false}
            filterElement={operatorFilterElement}
            sortable
            style={{ width: 100 }}
          />
        )}
        {isVisible('mains') && (
          <Column
            field="mainsLabel"
            header="Mains"
            filter showFilterMenu={false}
            filterElement={mainsFilterElement}
            style={{ width: 80 }}
            body={(row: GatewayRow) => yesNoIcon(row.batteryOperated, true)}
          />
        )}
        {isVisible('firmware') && (
          <Column field="firmware" header="Firmware" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 90 }} />
        )}
        {isVisible('publicAddress') && (
          <Column field="publicAddress" header="Public IP" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 130 }} />
        )}
        {isVisible('eth0ip') && (
          <Column field="eth0ip" header="ETH0 IP" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 110 }} />
        )}
        {isVisible('vpn2ip') && (
          <Column field="vpn2ip" header="TUN0 IP" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 110 }} />
        )}
        {isVisible('wwan0ip') && (
          <Column field="wwan0ip" header="WWAN0 IP" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 110 }} />
        )}
        {isVisible('gatewayip') && (
          <Column field="gatewayip" header="Gateway IP" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 110 }} />
        )}
        {isVisible('sim') && (
          <Column field="sim" header="SIM PIN" filter filterPlaceholder="" showFilterMenu={false} sortable style={{ width: 90 }} />
        )}
        {isVisible('apn') && (
          <Column
            field="apn"
            header="APN"
            filter showFilterMenu={false}
            filterElement={apnFilterElement}
            sortable
            style={{ width: 90 }}
          />
        )}
        {isVisible('simInserted') && (
          <Column
            field="simInsertedLabel"
            header="SIM inserted"
            filter showFilterMenu={false}
            filterElement={simInsertedFilterElement}
            style={{ width: 100 }}
            body={(row: GatewayRow) => yesNoIcon(row.simPresent)}
          />
        )}
        {isVisible('simPinUnlocked') && (
          <Column
            field="simPinUnlockedLabel"
            header="SIM PIN unlocked"
            filter showFilterMenu={false}
            filterElement={simPinUnlockedFilterElement}
            style={{ width: 110 }}
            body={(row: GatewayRow) => yesNoIcon(row.simPinRequired, true)}
          />
        )}
        {isVisible('status') && (
          <Column
            field="statusColor"
            header="Status"
            filter showFilterMenu={false}
            filterElement={statusFilterElement}
            style={{ width: 80 }}
            body={(row: GatewayRow) => renderStatusIcon(row)}
          />
        )}
      </DataTable>
      </div>
    </div>
  );
};

const actions = { getGateways };
const mapStateToProps = (state: any) => ({
  mobileView: state.dashboard.mobileView,
  gateways: state.dashboard.gateways,
});

export default connect(mapStateToProps, actions)(GatewaysList);